/**
 * Локализация интерфейса.
 *
 * Свой механизм в полсотни строк, а не библиотека: терминалу нужны
 * словарь, подстановка чисел и выбор языка. Всё остальное, что дают
 * готовые решения — загрузка по требованию, форматы дат, плюрализация
 * двадцати языков, — здесь не понадобится, а весит и усложняет.
 *
 * Ключ — не английская фраза, а путь: `feed.title`. Фраза в роли ключа
 * ломается при первой же правке текста, и переводы молча отваливаются;
 * путь переживает редактуру.
 *
 * Русский словарь полон по определению — он же исходный текст. В
 * английском может не хватать ключа: тогда показывается русский, а не
 * пустота и не сам ключ. Человек, увидевший в английском интерфейсе
 * русскую строчку, поймёт больше, чем увидевший `feed.title`.
 *
 * Чего здесь нет: тексты, которые порождает сервер, — разбор рынка и
 * объяснения правил. Они собираются в Core и `@specter/rules` на
 * русском; их перевод — отдельная работа, и делать вид, что она сделана,
 * нельзя.
 */

import type { LanguageSetting } from '@specter/types';
import { en } from './en.js';
import { ru } from './ru.js';

export type Language = 'ru' | 'en';
export type Dictionary = Record<string, string>;

const DICTIONARIES: Record<Language, Dictionary> = { ru, en };

let current: Language = 'ru';
const listeners = new Set<() => void>();

/** Язык браузера, если человек не выбрал свой. */
function systemLanguage(): Language {
  if (typeof navigator === 'undefined') return 'ru';
  return navigator.language.toLowerCase().startsWith('ru') ? 'ru' : 'en';
}

export function resolveLanguage(setting: LanguageSetting): Language {
  return setting === 'system' ? systemLanguage() : setting;
}

export function activeLanguage(): Language {
  return current;
}

/** Сменить язык. Возвращает `true`, если он действительно изменился. */
export function setLanguage(setting: LanguageSetting): boolean {
  const next = resolveLanguage(setting);
  if (next === current) return false;

  current = next;
  // Атрибут на документе — побочный эффект для браузера: он нужен
  // подбору шрифтов и переносам. Словарь работает и без DOM, поэтому
  // отсутствие документа здесь не ошибка.
  if (typeof document !== 'undefined') document.documentElement.lang = next;
  for (const listener of listeners) listener();
  return true;
}

export function onLanguageChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Строка по ключу.
 *
 * Подстановка простая: `{name}` заменяется на значение. Ни выражений,
 * ни форматирования — всё, что сложнее, считается до вызова, где виден
 * контекст.
 */
export function t(key: string, values?: Record<string, string | number>): string {
  const dictionary = DICTIONARIES[current];
  // Английского перевода может не быть — тогда русский. Ключ на экране
  // не показывается никогда: это отладочная строка, а не текст.
  const template = dictionary[key] ?? ru[key] ?? key;
  if (!values) return template;

  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = values[name];
    return value === undefined ? match : String(value);
  });
}

/**
 * Согласование числительного.
 *
 * В русском три формы, в английском две — и это единственное место, где
 * разница между языками не сводится к словарю.
 */
export function plural(count: number, forms: readonly [string, string, string]): string {
  if (current === 'en') return count === 1 ? forms[0] : forms[2];

  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod100 >= 11 && mod100 <= 14) return forms[2];
  if (mod10 === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}

/** Ключи, которых нет в английском словаре, — для проверки. */
export function missingKeys(): string[] {
  return Object.keys(ru).filter((key) => !(key in en));
}
